import { useRef, useCallback, useEffect, useState } from "react";
import { Logger } from "@cede/utils";
import { THROTTLE_DELAY, MAX_RECONNECT_ATTEMPTS, MAX_RECONNECT_DELAY } from "../constants";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";

const EXCHANGE_ID = "kraken";
const WS_URL = "wss://ws.kraken.com/v2";

// Orderbook state - maintained at module level to persist across re-renders
const orderbookState: {
  bids: Map<string, string>;
  asks: Map<string, string>;
  currentSymbol: string | null;
} = {
  bids: new Map(),
  asks: new Map(),
  currentSymbol: null,
};

const ORDERBOOK_DEPTH = 25;

/**
 * Hook for connecting to Kraken WebSocket v2 and subscribing to orderbook updates
 * Kraken uses book channel with snapshot + update pattern
 * First message is a snapshot, subsequent messages are updates (qty=0 means remove)
 */
export const useKrakenOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const isConnecting = useRef(false);
  const reconnectAttemptsRef = useRef<number>(0);

  const [isConnected, setIsConnected] = useState(false);
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateOrderBookData = useCallback((data: DirectOrderBookSnapshot) => {
    setOrderBookData(data);
    setError(null);
  }, []);

  const handleOrderBookUpdate = useCallback(
    (data: DirectOrderBookSnapshot) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        lastUpdateTimeRef.current = now;
        updateOrderBookData(data);
      } else {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }

        const remainingTime = THROTTLE_DELAY - timeSinceLastUpdate;
        throttleTimeoutRef.current = setTimeout(() => {
          lastUpdateTimeRef.current = Date.now();
          updateOrderBookData(data);
        }, remainingTime);
      }
    },
    [updateOrderBookData],
  );

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    isConnecting.current = false;
    setIsConnected(false);
    lastUpdateTimeRef.current = 0;
  }, []);

  const subscribeToOrderBook = useCallback((ws: WebSocket, symbol: string) => {
    // Kraken WebSocket v2 subscription format
    // Depth options: 10, 25, 100, 500, 1000 (default: 10)
    const subscribeMessage = {
      method: "subscribe",
      params: {
        channel: "book",
        symbol: [symbol],
        depth: ORDERBOOK_DEPTH,
      },
    };

    ws.send(JSON.stringify(subscribeMessage));
  }, []);

  /**
   * Convert module-level maps to sorted arrays and create snapshot
   */
  const createSnapshotFromState = useCallback((symbol: string): DirectOrderBookSnapshot => {
    // Convert bids map to sorted array (highest price first), limit to top 50
    const bids: [number, number][] = Array.from(orderbookState.bids.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => b[0] - a[0])
      .slice(0, ORDERBOOK_DEPTH);

    // Convert asks map to sorted array (lowest price first), limit to top 50
    const asks: [number, number][] = Array.from(orderbookState.asks.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => a[0] - b[0])
      .slice(0, ORDERBOOK_DEPTH);

    return {
      timestamp: Date.now(),
      exchangeId: EXCHANGE_ID,
      pairSymbol: symbol,
      bids,
      asks,
    };
  }, []);

  const connectWs = useCallback(() => {
    if (isConnecting.current || !enabled || !pairSymbol) return;

    disconnectWs();

    // Reset orderbook state only if symbol changed
    if (orderbookState.currentSymbol !== pairSymbol) {
      orderbookState.bids = new Map();
      orderbookState.asks = new Map();
      orderbookState.currentSymbol = pairSymbol;
    }

    isConnecting.current = true;
    setError(null);
    setOrderBookData(null);

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        setIsConnected(true);
        isConnecting.current = false;
        reconnectAttemptsRef.current = 0;

        // Subscribe to orderbook
        const symbol = pairSymbol;
        subscribeToOrderBook(ws, symbol);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle subscription response or status messages
          if (message.method === "subscribe" || message.channel === "status" || message.channel === "heartbeat") {
            return;
          }

          // Handle orderbook data
          // Kraken v2 format: { channel: "book", type: "snapshot"|"update", data: [{ symbol, bids: [{price, qty}], asks: [{price, qty}] }] }
          if (message.channel === "book" && message.data && message.data.length > 0) {
            const bookData = message.data[0];
            if (bookData.symbol !== pairSymbol) {
              return;
            }
            const isSnapshot = message.type === "snapshot";

            // On snapshot, reset state
            if (isSnapshot) {
              orderbookState.bids = new Map();
              orderbookState.asks = new Map();
            }

            // Apply bids: Kraken v2 format [{price: number, qty: number}, ...]
            if (bookData.bids) {
              for (const level of bookData.bids as { price: number; qty: number }[]) {
                const price = String(level.price);
                const qty = level.qty;
                if (qty === 0) {
                  orderbookState.bids.delete(price);
                } else {
                  orderbookState.bids.set(price, String(qty));
                }
              }
            }

            // Apply asks: Kraken v2 format [{price: number, qty: number}, ...]
            if (bookData.asks) {
              for (const level of bookData.asks as { price: number; qty: number }[]) {
                const price = String(level.price);
                const qty = level.qty;
                if (qty === 0) {
                  orderbookState.asks.delete(price);
                } else {
                  orderbookState.asks.set(price, String(qty));
                }
              }
            }

            // Create snapshot from updated state
            const snapshot = createSnapshotFromState(pairSymbol);
            handleOrderBookUpdate(snapshot);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to parse orderbook update:`, errorMessage, err);
          setError("Failed to parse orderbook update");
        }
      };

      ws.onerror = () => {
        isConnecting.current = false;
        setIsConnected(false);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        isConnecting.current = false;
        setIsConnected(false);

        if (enabled && pairSymbol && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY);
          reconnectAttemptsRef.current++;

          Logger.log(
            `[${EXCHANGE_ID}OrderBookWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
            Logger.types.INFO,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWs();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Max reconnection attempts reached, giving up`);
          setError("Failed to reconnect after multiple attempts");
        }
      };

      wsRef.current = ws;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to create WebSocket connection:`, errorMessage, err);
      isConnecting.current = false;
      setError("Failed to create WebSocket connection");
    }
  }, [pairSymbol, enabled, disconnectWs, handleOrderBookUpdate, subscribeToOrderBook, createSnapshotFromState]);

  useEffect(() => {
    if (!enabled || !pairSymbol) {
      disconnectWs();
      return;
    }

    connectWs();

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectWs();
      } else if (enabled && pairSymbol) {
        connectWs();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
      disconnectWs();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [pairSymbol, enabled, connectWs, disconnectWs]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error,
  };
};
